import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Hls from 'hls.js';
import axios from '../axios';
import { getURL } from 'src/utils/config';
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import Replay10RoundedIcon from '@mui/icons-material/Replay10Rounded';
import Forward10RoundedIcon from '@mui/icons-material/Forward10Rounded';
import SkipPreviousRoundedIcon from '@mui/icons-material/SkipPreviousRounded';
import SkipNextRoundedIcon from '@mui/icons-material/SkipNextRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import VolumeDownRoundedIcon from '@mui/icons-material/VolumeDownRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import DragHandleSharp from '@mui/icons-material/DragHandleSharp';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDateTimePicker } from '@mui/x-date-pickers/DesktopDateTimePicker';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import Logo from '../components/Logo';

const SPEEDS = [0.5, 1, 1.5, 2, 3, 4, 5, 6, 8, 10];
const DEFAULT_VIDEO = null;

function getTickIntervals(vm) {
  if (vm <= 15) return { small: 30, medium: 60, large: 300 };
  if (vm <= 30) return { small: 60, medium: 300, large: 600 };
  if (vm <= 60) return { small: 300, medium: 600, large: 1800 };
  if (vm <= 360) return { small: 900, medium: 1800, large: 3600 };
  return { small: 1800, medium: 3600, large: 7200 };
}

function getLabelInterval(vm) {
  if (vm <= 15) return 900;
  if (vm <= 30) return 1800;
  return 3600;
}

const controlBtnSx = {
  width: 30,
  height: 30,
  color: 'text.secondary',
  '& .MuiSvgIcon-root': { fontSize: 20 },
  '&:hover': { color: 'text.primary', bgcolor: 'action.hover' }
};

function getClipWindow(videoUrl) {
  const fallbackStart = dayjs().startOf('day').add(23, 'hour').add(10, 'minute');
  const fallbackEnd = fallbackStart.add(10, 'minute');

  if (!videoUrl) return { start: fallbackStart, end: fallbackEnd };

  try {
    const parsed = new URL(videoUrl);
    const fromTs = Number(parsed.searchParams.get('from_ts'));
    const rawDuration = Number(parsed.searchParams.get('duration'));
    const duration = Number.isFinite(rawDuration) && rawDuration > 0 ? Math.min(rawDuration, 15 * 60) : 10 * 60;

    if (Number.isFinite(fromTs) && fromTs > 0) {
      const start = dayjs.unix(fromTs);
      return { start, end: start.add(duration, 'second') };
    }
  } catch (_error) {
    // ignore malformed URL and use fallback
  }

  return { start: fallbackStart, end: fallbackEnd };
}

export default function PlaybackVideoPlayer({
  sourceUrl,
  embedded = false,
  onClose,
  cameraLabel = 'Playback Camera',
  cameraId = null,
  currentClip,
  onPrevChunk,
  onNextChunk,
  hasPrevChunk = false,
  hasNextChunk = false,
  timelineData = [],
  onDateChange,
  onSelectClip,
  seekTarget = null,
  timelineDay = null,
  loadedRange = null,
  onPlaylistEnd,
  loading = false,
  sharedMode = false,
  availableZoomLevels = [
    { m: 15, label: '15m' },
    { m: 30, label: '30m' },
    { m: 60, label: '1h' },
    { m: 360, label: '6h' },
    { m: 720, label: '12h' },
  ],
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const videoRef = useRef(null);
  const timelineRef = useRef(null);
  const timelineInnerRef = useRef(null);
  const timelineTicksRef = useRef(null);
  const timeLabelRef = useRef(null);
  const currentTimeRef = useRef(0);
  const lastStateUpdateRef = useRef(0);
  const visibleMinutesRef = useRef(15);
  const seekTargetRef = useRef(null);
  const hoverLineRef = useRef(null);
  const hoverLabelRef = useRef(null);
  // Live mirror of the timeline day anchor so the rAF loop reads the current value
  // without being re-created (and without going stale) when the anchor changes.
  const dayStartRef = useRef(null);

  const [duration, setDuration] = useState(0);
  const [timelineWidth, setTimelineWidth] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(3);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerDraft, setPickerDraft] = useState(null);
  const [playbackError, setPlaybackError] = useState('');
  const [isBuffering, setIsBuffering] = useState(false);
  // How much of the current clip is actually buffered/cached in the video element,
  // mapped to timeline day-seconds — shown as the lighter "cache" part of the bar.
  const [bufferedRanges, setBufferedRanges] = useState([]);
  // True while the user is dragging the timeline. Used to suppress the loading
  // spinner so rapid seek waiting/playing events don't make the video "blink".
  const [isScrubbing, setIsScrubbing] = useState(false);
  // Fullscreen: whole custom player (timeline + controls) goes fullscreen, and the
  // header/control bar auto-hide when idle, reappearing on mouse move.
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const controlsHideTimerRef = useRef(null);
  const [visibleMinutes, setVisibleMinutes] = useState(() => availableZoomLevels[0]?.m ?? 15);
  const [speedAnchor, setSpeedAnchor] = useState(null);

  // Share panel state
  const [shareOpen, setShareOpen] = useState(false);
  const [shareStart, setShareStart] = useState(null);
  const [shareEnd, setShareEnd] = useState(null);
  const [shareExpiry, setShareExpiry] = useState('1 day');
  const [shareReceiverName, setShareReceiverName] = useState('');
  const [shareEmail, setShareEmail] = useState('');
  const [shareComments, setShareComments] = useState('');
  const [shareSending, setShareSending] = useState(false);
  const [shareError, setShareError] = useState('');
  const sharePanelRef = useRef(null);
  const rangeHandleDragging = useRef(null); // 'start' | 'end' | null
  const rangeHandleDragStartX = useRef(0);
  const rangeHandleDragStartTimes = useRef({ start: null, end: null });
  const timelineDragRef = useRef({ active: false, startX: 0, startTime: 0, didDrag: false });
  const containerRef = useRef(null); // outermost player element — the fullscreen target
  // Throttle the actual video seek to one per animation frame while scrubbing so the
  // timeline can track the pointer smoothly without stalling the (HLS) decoder.
  const pendingSeekRef = useRef(null);
  const seekRafRef = useRef(null);
  // While a click-to-load is in flight, hold the timeline at the glided-to target so
  // the rAF loop (still running on the outgoing clip) doesn't yank it back. Cleared
  // once the new clip has loaded and seeked to the target.
  const holdTimelineRef = useRef(false);
  const holdTimelineTimerRef = useRef(null);

  const videoSrc = sourceUrl || searchParams.get('video') || DEFAULT_VIDEO;
  // console.info('[videoSrc]', videoSrc, '| sourceUrl prop:', sourceUrl);
  const isHlsSource = /\.m3u8($|\?)/i.test(videoSrc);

  const clipWindow = useMemo(() => {
    if (currentClip?.recording_start_time && currentClip?.recording_end_time) {
      const start = dayjs(currentClip.recording_start_time);
      const end = dayjs(currentClip.recording_end_time);
      if (start.isValid() && end.isValid() && end.isAfter(start)) {
        return { start, end };
      }
    }
    return getClipWindow(videoSrc);
  }, [videoSrc, currentClip]);
  const clipWindowDurationSeconds = useMemo(() => {
    const secs = clipWindow.end.diff(clipWindow.start, 'second');
    return Math.max(1, secs);
  }, [clipWindow]);

  // Base for mapping video-time → clip-window time. Read the media element's own
  // duration live rather than the `duration` state: the rAF loop's closure is only
  // rebuilt when the source changes — before the new clip's metadata has loaded —
  // so the state it captures is the *previous* clip's duration. Falling back to the
  // window length only until metadata arrives; never max() the two, or a clip whose
  // media is shorter than its requested window maps at the wrong rate.
  const getMappingDuration = () => {
    const d = videoRef.current?.duration;
    return Number.isFinite(d) && d > 0 ? d : clipWindowDurationSeconds;
  };

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;
    let hls;

    setPlaybackError('');
    setIsBuffering(true);
    // Drop the outgoing clip's timing so nothing maps against it while the new
    // clip's metadata is still loading.
    setDuration(0);
    setCurrentTime(0);

    if (isHlsSource) {
      if (Hls.isSupported()) {
        hls = new Hls();
        hls.loadSource(videoSrc);
        hls.attachMedia(el);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          el.playbackRate = playbackRate;
          el.play().catch(() => {});
        });
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data?.fatal) {
            const detail = data.type === Hls.ErrorTypes.NETWORK_ERROR
              ? 'Network error loading HLS stream.'
              : data.type === Hls.ErrorTypes.MEDIA_ERROR
                ? 'Media error — stream may be unsupported.'
                : 'Unable to play this HLS stream.';
            setPlaybackError(detail);
          }
        });
      } else if (el.canPlayType('application/vnd.apple.mpegurl')) {
        el.src = videoSrc;
      } else {
        setPlaybackError('HLS is not supported in this browser.');
      }
    } else {
      el.src = videoSrc;
    }

    const onLoaded = () => {
      const loadedDuration = Number.isFinite(el.duration) ? el.duration : 0;
      setDuration(loadedDuration);
      setCurrentTime(el.currentTime || 0);
      // Restore playback state on chunk change
      el.playbackRate = playbackRate;
      el.volume = isMuted ? el.volume : volume;

      // If a seek target was requested (e.g. picker selected a different date),
      // jump to that position within this clip before playing
      const target = seekTargetRef.current;
      if (target && target.isValid()) {
        const clipDurSec = clipWindow.end.diff(clipWindow.start, 'second');
        const offsetSec = target.diff(clipWindow.start, 'second');
        console.info('[onLoaded] seekTarget:', target.format('YYYY-MM-DD HH:mm:ss'));
        console.info('[onLoaded] clipWindow:', clipWindow.start.format('YYYY-MM-DD HH:mm:ss'), '→', clipWindow.end.format('YYYY-MM-DD HH:mm:ss'));
        console.info('[onLoaded] offsetSec:', offsetSec, '/ clipDurSec:', clipDurSec, '| loadedDuration:', loadedDuration);
        if (offsetSec >= 0 && offsetSec <= clipDurSec) {
          const baseDur = loadedDuration > 0 ? loadedDuration : clipDurSec;
          el.currentTime = (offsetSec / clipDurSec) * baseDur;
          console.info('[onLoaded] el.currentTime set to:', el.currentTime);
        } else {
          console.info('[onLoaded] offsetSec out of range — not seeking');
        }
        seekTargetRef.current = null;
      }

      // The new clip is in place at the target — release the timeline hold so the
      // rAF loop takes over from here (clearing the glide transition first so
      // playback tracking is instant, not eased).
      if (holdTimelineRef.current) {
        holdTimelineRef.current = false;
        if (holdTimelineTimerRef.current) { clearTimeout(holdTimelineTimerRef.current); holdTimelineTimerRef.current = null; }
        if (timelineInnerRef.current) timelineInnerRef.current.style.transition = 'none';
        if (timelineTicksRef.current) timelineTicksRef.current.style.transition = 'none';
      }

      // Keep muted state as-is; don't override to allow autoplay to proceed
      if (!isHlsSource) {
        el.play().catch(() => {});
      }
    };

    // rAF loop — direct DOM updates for smooth animation, throttled React state
    let rafId;
    const tick = () => {
      const t = el.currentTime || 0;
      currentTimeRef.current = t;
      const baseDur = getMappingDuration();

      // Direct DOM: update both scrolling inner boxes (unless a click-to-load is
      // gliding the timeline to its target — don't fight that animation).
      if (timelineRef.current && !holdTimelineRef.current) {
        const w = timelineRef.current.offsetWidth;
        const pxS = w / (visibleMinutesRef.current * 60);
        const anchor = dayStartRef.current || clipWindow.start.startOf('day');
        const elapsed = Math.floor((t / baseDur) * clipWindowDurationSeconds);
        const daySec = clipWindow.start.add(elapsed, 'second').diff(anchor, 'second');
        const tx = `translateX(${w / 2 - daySec * pxS}px)`;
        if (timelineInnerRef.current) timelineInnerRef.current.style.transform = tx;
        if (timelineTicksRef.current) timelineTicksRef.current.style.transform = tx;
      }

      // Direct DOM: update time label
      if (timeLabelRef.current) {
        const mapped = clipWindow.start.add(
          Math.floor((t / baseDur) * clipWindowDurationSeconds),
          'second'
        );
        timeLabelRef.current.textContent = mapped.format('HH:mm:ss');
      }

      // Throttle React state update to every 250ms (for slider/picker)
      const now = performance.now();
      if (now - lastStateUpdateRef.current > 250) {
        lastStateUpdateRef.current = now;
        setCurrentTime(t);
      }

      rafId = requestAnimationFrame(tick);
    };

    const onPlay = () => { setIsPlaying(true); rafId = requestAnimationFrame(tick); };
    const onPause = () => {
      setIsPlaying(false);
      cancelAnimationFrame(rafId);
      setCurrentTime(el.currentTime || 0);
    };
    // hls.js can revise the duration after the manifest is parsed, so track it
    // rather than trusting the single value read at loadedmetadata.
    const onDurationChange = () => {
      const d = el.duration;
      setDuration(Number.isFinite(d) && d > 0 ? d : 0);
    };
    const onWaiting = () => setIsBuffering(true);
    const onPlaying = () => setIsBuffering(false);
    const onCanPlay = () => { setIsBuffering(false); updateBuffered(); };
    const onError = () => { setIsBuffering(false); setPlaybackError('Unable to play this video source.'); };

    // Map the video element's buffered TimeRanges (video-time) onto the timeline
    // (day-seconds), so we can show how much of the current clip is cached.
    const updateBuffered = () => {
      const b = el.buffered;
      const baseDur = el.duration || clipWindowDurationSeconds;
      if (!b || b.length === 0 || baseDur <= 0) { setBufferedRanges([]); return; }
      const anchor = dayStartRef.current || clipWindow.start.startOf('day');
      const out = [];
      for (let i = 0; i < b.length; i++) {
        const off0 = (b.start(i) / baseDur) * clipWindowDurationSeconds;
        const off1 = (b.end(i) / baseDur) * clipWindowDurationSeconds;
        const s = clipWindow.start.add(off0, 'second').diff(anchor, 'second');
        const e = clipWindow.start.add(off1, 'second').diff(anchor, 'second');
        out.push({ startDaySec: s, durationSec: Math.max(e - s, 1) });
      }
      setBufferedRanges(out);
    };
    const onEnded = () => {
      cancelAnimationFrame(rafId);
      if (onNextChunk && hasNextChunk) {
        onNextChunk();
      } else {
        setIsPlaying(false);
        if (onPlaylistEnd) onPlaylistEnd();
      }
    };

    el.addEventListener('loadedmetadata', onLoaded);
    el.addEventListener('durationchange', onDurationChange);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('waiting', onWaiting);
    el.addEventListener('playing', onPlaying);
    el.addEventListener('canplay', onCanPlay);
    el.addEventListener('progress', updateBuffered);
    el.addEventListener('error', onError);
    el.addEventListener('ended', onEnded);
    setBufferedRanges([]); // reset for the new clip

    return () => {
      cancelAnimationFrame(rafId);
      el.removeEventListener('loadedmetadata', onLoaded);
      el.removeEventListener('durationchange', onDurationChange);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('waiting', onWaiting);
      el.removeEventListener('playing', onPlaying);
      el.removeEventListener('canplay', onCanPlay);
      el.removeEventListener('progress', updateBuffered);
      el.removeEventListener('error', onError);
      el.removeEventListener('ended', onEnded);
      if (hls) hls.destroy();
      el.removeAttribute('src');
      el.load();
    };
  }, [videoSrc, isHlsSource]);

  // Keep seekTargetRef in sync with the seekTarget prop so onLoaded can access it
  useEffect(() => {
    seekTargetRef.current = seekTarget;
  }, [seekTarget]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.playbackRate = playbackRate;
  }, [playbackRate]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = isMuted;
    if (!isMuted) el.volume = volume;
  }, [isMuted, volume]);

  useEffect(() => { visibleMinutesRef.current = visibleMinutes; }, [visibleMinutes]);

  // Zoom is only adjustable while the share panel is open; reset to the default
  // (15m) whenever it's closed.
  useEffect(() => {
    if (!shareOpen) setVisibleMinutes(availableZoomLevels[0]?.m ?? 15);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareOpen]);

  useEffect(() => {
    const el = timelineRef.current;
    if (!el) return undefined;
    const obs = new ResizeObserver((entries) => {
      setTimelineWidth(entries[0].contentRect.width);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || document.activeElement?.isContentEditable) return;

      const el = videoRef.current;
      if (!el) return;

      switch (e.key) {
        case ' ':
        case 'k':
          e.preventDefault();
          el.paused ? el.play().catch(() => {}) : el.pause();
          break;
        case 'ArrowLeft':
        case 'j':
          e.preventDefault();
          seekTo(currentTimeRef.current - 10);
          break;
        case 'ArrowRight':
        case 'l':
          e.preventDefault();
          seekTo(currentTimeRef.current + 10);
          break;
        case 'ArrowUp':
          e.preventDefault();
          onVolumeChange(null, Math.min(1, el.volume + 0.1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          onVolumeChange(null, Math.max(0, el.volume - 0.1));
          break;
        case 'm':
          toggleMute();
          break;
        case 'f':
          enterFullScreen();
          break;
        case ',':
          if (hasPrevChunk && onPrevChunk) onPrevChunk();
          break;
        case '.':
          if (hasNextChunk && onNextChunk) onNextChunk();
          break;
        default:
          break;
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [hasPrevChunk, hasNextChunk, onPrevChunk, onNextChunk]);

  const mappedTimelineDate = useMemo(() => {
    // Same mapping rule as getMappingDuration, but off state so the memo stays
    // reactive (a ref read wouldn't re-trigger it).
    const base = duration > 0 ? duration : clipWindowDurationSeconds;
    const progress = Math.max(0, Math.min(1, currentTime / base));
    return clipWindow.start.add(Math.floor(progress * clipWindowDurationSeconds), 'second');
  }, [currentTime, duration, clipWindow, clipWindowDurationSeconds]);

  // 24-hour timeline: configurable visible window centered on current time.
  // Anchor to the selected calendar day (`timelineDay`) so the ruler always shows
  // that day — even when the first clip's footage begins on the previous day
  // (a clip that spans midnight). Fall back to the clip's own start day when the
  // parent doesn't supply an anchor.
  const VISIBLE_SECONDS = visibleMinutes * 60;
  const dayStart = useMemo(
    () =>
      timelineDay && dayjs(timelineDay).isValid()
        ? dayjs(timelineDay).startOf('day')
        : clipWindow.start.startOf('day'),
    [timelineDay, clipWindow]
  );
  dayStartRef.current = dayStart;
  const currentDaySec = useMemo(() => mappedTimelineDate.diff(dayStart, 'second'), [mappedTimelineDate, dayStart]);
  const pxPerSec = timelineWidth > 0 ? timelineWidth / VISIBLE_SECONDS : 0;
  // Extend inner width past 24h if clip crosses midnight
  const DAY_SECS = 24 * 60 * 60;
  const clipEndDaySec = useMemo(() => clipWindow.end.diff(dayStart, 'second'), [clipWindow, dayStart]);
  const timelineTotalSecs = Math.max(DAY_SECS, clipEndDaySec + 3600);
  const innerWidth = timelineTotalSecs * pxPerSec;
  const translateX = timelineWidth / 2 - currentDaySec * pxPerSec;

  // The rAF loop / seek / picker set the timeline transform imperatively (inline
  // style), which overrides the React-driven transform. While playing the rAF loop
  // keeps re-applying it with the current width, but while paused it goes stale — so
  // when the layout changes (e.g. opening the share panel resizes the timeline) the
  // playhead ends up off its real position. Re-apply the correct transform whenever
  // it changes while paused.
  useEffect(() => {
    if (isPlaying) return; // rAF loop keeps the transform correct while playing
    if (holdTimelineRef.current) return; // a click-to-load glide owns the transform
    const tx = `translateX(${translateX}px)`;
    if (timelineInnerRef.current) { timelineInnerRef.current.style.transition = 'none'; timelineInnerRef.current.style.transform = tx; }
    if (timelineTicksRef.current) { timelineTicksRef.current.style.transition = 'none'; timelineTicksRef.current.style.transform = tx; }
    // Keyed on width (not translateX) so a paused click-to-seek keeps its animation;
    // this only re-syncs when the timeline is resized (e.g. share panel opens/closes).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineWidth, isPlaying]);

  // Bounds (in seconds-from-dayStart) of the time span actually fetched into the
  // player. Everything outside this is "not loaded yet" (fetch on click); gaps
  // inside it are genuinely "no recording". When no range is supplied (e.g. shared
  // links) treat the whole visible scale as loaded so nothing is dimmed.
  const loadedStartSec = useMemo(
    () => (loadedRange?.start ? dayjs(loadedRange.start).diff(dayStart, 'second') : -DAY_SECS),
    [loadedRange, dayStart, DAY_SECS]
  );
  const loadedEndSec = useMemo(
    () => (loadedRange?.end ? dayjs(loadedRange.end).diff(dayStart, 'second') : timelineTotalSecs + DAY_SECS),
    [loadedRange, dayStart, timelineTotalSecs, DAY_SECS]
  );

  // Latest selectable moment is "now" — footage only exists up to the present, and
  // the picker must not be capped to the currently-loaded window (data is fetched
  // lazily one hour at a time), otherwise later times couldn't be selected.
  const pickerMaxDateTime = useMemo(() => dayjs(), []);

  const segmentBoxes24 = useMemo(() => {
    if (!Array.isArray(timelineData) || timelineData.length === 0) return [];
    return timelineData.map((item) => {
      try {
        const url = item?.local_play_url || item?.play_url || item?.VideoUrl;
        if (!url) return null;
        let segStart = null;
        let segEnd = null;
        const isEvent = String(item?.DetectedType || '').toLowerCase() === 'event';

        if (!isEvent) {
          const baseStart = item?.CreatedOn ? dayjs(item.CreatedOn) : null;
          if (baseStart && baseStart.isValid()) {
            segStart = baseStart;
            segEnd = baseStart.add(30, 'second');
          } else if (item?.recording_start_time && item?.recording_end_time) {
            const s = dayjs(item.recording_start_time);
            const e = dayjs(item.recording_end_time);
            if (s.isValid() && e.isValid() && e.isAfter(s)) { segStart = s; segEnd = e; }
          }
          if (!segStart || !segEnd) {
            const parsed = new URL(url);
            const fromTs = Number(parsed.searchParams.get('from_ts'));
            const rawDur = Number(parsed.searchParams.get('duration'));
            if (!Number.isFinite(fromTs) || fromTs <= 0) return null;
            segStart = dayjs.unix(fromTs);
            segEnd = segStart.add(Number.isFinite(rawDur) && rawDur > 0 ? rawDur : 900, 'second');
          }
        } else {
          if (item?.recording_start_time && item?.recording_end_time) {
            const s = dayjs(item.recording_start_time);
            const e = dayjs(item.recording_end_time);
            if (s.isValid() && e.isValid() && e.isAfter(s)) { segStart = s; segEnd = e; }
          }
          if (!segStart || !segEnd) {
            const parsed = new URL(url);
            const fromTs = Number(parsed.searchParams.get('from_ts'));
            const rawDur = Number(parsed.searchParams.get('duration'));
            if (!Number.isFinite(fromTs) || fromTs <= 0) return null;
            segStart = dayjs.unix(fromTs);
            segEnd = segStart.add(Number.isFinite(rawDur) && rawDur > 0 ? rawDur : 900, 'second');
          }
        }

        // Position every segment against the shared timeline anchor (`dayStart`),
        // not its own day — a clip that began on the previous day then lands at a
        // negative offset (left of midnight) instead of being wrapped into 0–24h.
        const startDaySec = segStart.diff(dayStart, 'second');
        const durationSec = Math.max(segEnd.diff(segStart, 'second'), 30);
        return { startDaySec, durationSec, isEvent };
      } catch (_e) { return null; }
    }).filter(Boolean);
  }, [timelineData, dayStart]);

  // "No recording" gaps — confined to the loaded window. Outside it we don't yet
  // know whether footage exists, so those areas are shown as "not loaded" instead.
  const gapZones = useMemo(() => {
    // With a fetched window, confine "no recording" to it; without one, fall back
    // to the single selected day (0–24h) as before.
    const winStart = loadedRange ? loadedStartSec : 0;
    const winEnd = loadedRange ? loadedEndSec : DAY_SECS;
    if (winEnd <= winStart) return [];
    const inWindow = segmentBoxes24
      .filter((s) => s.startDaySec + s.durationSec > winStart && s.startDaySec < winEnd)
      .sort((a, b) => a.startDaySec - b.startDaySec);
    if (inWindow.length === 0) {
      return [{ startDaySec: winStart, durationSec: winEnd - winStart }];
    }
    const gaps = [];
    let cursor = winStart;
    for (const seg of inWindow) {
      if (seg.startDaySec > cursor) {
        gaps.push({ startDaySec: cursor, durationSec: seg.startDaySec - cursor });
      }
      cursor = Math.max(cursor, seg.startDaySec + seg.durationSec);
    }
    if (cursor < winEnd) gaps.push({ startDaySec: cursor, durationSec: winEnd - cursor });
    return gaps;
  }, [segmentBoxes24, loadedRange, loadedStartSec, loadedEndSec, DAY_SECS]);

  // "Not loaded yet" zones — the parts of the visible scale outside the fetched
  // window. Clicking one asks the parent to fetch a window at that time.
  const notLoadedZones = useMemo(() => {
    if (!loadedRange) return [];
    const dispStart = -DAY_SECS;
    const dispEnd = timelineTotalSecs + DAY_SECS;
    const zones = [];
    if (loadedStartSec > dispStart) {
      zones.push({ startDaySec: dispStart, durationSec: loadedStartSec - dispStart });
    }
    if (loadedEndSec < dispEnd) {
      zones.push({ startDaySec: loadedEndSec, durationSec: dispEnd - loadedEndSec });
    }
    return zones;
  }, [loadedRange, loadedStartSec, loadedEndSec, timelineTotalSecs, DAY_SECS]);

  // Merge contiguous recording clips into continuous runs so the timeline reads as
  // one plain line instead of exposing every ~15-minute clip boundary to the user.
  // A few seconds of tolerance bridges rounding between back-to-back clips; real
  // (minute-scale) recording gaps still break the line.
  const recordingRuns = useMemo(() => {
    const MERGE_TOL = 3; // seconds
    const segs = segmentBoxes24
      .filter((s) => !s.isEvent)
      .map((s) => ({ start: s.startDaySec, end: s.startDaySec + s.durationSec }))
      .sort((a, b) => a.start - b.start);
    const runs = [];
    for (const s of segs) {
      const last = runs[runs.length - 1];
      if (last && s.start <= last.end + MERGE_TOL) {
        last.end = Math.max(last.end, s.end);
      } else {
        runs.push({ start: s.start, end: s.end });
      }
    }
    return runs.map((r) => ({ startDaySec: r.start, durationSec: r.end - r.start }));
  }, [segmentBoxes24]);

  const handleTimelineMouseDown = (e) => {
    timelineDragRef.current = { active: true, startX: e.clientX, startTime: currentTimeRef.current, didDrag: false };
    if (timelineRef.current) timelineRef.current.style.cursor = 'grabbing';
  };

  // Smooth scrub while dragging: move the timeline (and time label) via direct DOM
  // with no transition and no React re-render, and coalesce the video seek to one
  // per frame. Committing to el.currentTime + React state happens on mouse-up.
  const scrubTimeline = (newTime, baseDur) => {
    if (!timelineRef.current) return;
    currentTimeRef.current = newTime;
    const w = timelineRef.current.offsetWidth;
    const pxS = w / (visibleMinutesRef.current * 60);
    if (pxS <= 0) return;
    const anchor = dayStartRef.current || clipWindow.start.startOf('day');
    const elapsed = Math.floor((newTime / baseDur) * clipWindowDurationSeconds);
    const mapped = clipWindow.start.add(elapsed, 'second');
    const daySec = mapped.diff(anchor, 'second');
    const tx = `translateX(${w / 2 - daySec * pxS}px)`;
    if (timelineInnerRef.current) {
      timelineInnerRef.current.style.transition = 'none';
      timelineInnerRef.current.style.transform = tx;
    }
    if (timelineTicksRef.current) {
      timelineTicksRef.current.style.transition = 'none';
      timelineTicksRef.current.style.transform = tx;
    }
    if (timeLabelRef.current) timeLabelRef.current.textContent = mapped.format('HH:mm:ss');
    // Coalesce the (expensive) video seek to the latest position, once per frame.
    pendingSeekRef.current = newTime;
    if (!seekRafRef.current) {
      seekRafRef.current = requestAnimationFrame(() => {
        seekRafRef.current = null;
        const el = videoRef.current;
        if (el && pendingSeekRef.current != null) el.currentTime = pendingSeekRef.current;
      });
    }
  };

  // Glide the timeline so `absTime` sits under the centre playhead, and hold it
  // there while the (possibly not-yet-loaded) clip loads — so selecting a time
  // feels like one smooth seek instead of a wait-then-jump. The hold is released
  // in onLoaded once the new clip has seeked to the target.
  const glideTimelineToTime = (absTime) => {
    if (!timelineRef.current) return;
    const w = timelineRef.current.offsetWidth;
    const pxS = w / (visibleMinutesRef.current * 60);
    if (pxS <= 0) return;
    const anchor = dayStartRef.current || absTime.startOf('day');
    const daySec = absTime.diff(anchor, 'second');
    const tx = `translateX(${w / 2 - daySec * pxS}px)`;
    const EASE = 'transform 0.4s cubic-bezier(0.4,0,0.2,1)';
    holdTimelineRef.current = true;
    if (timelineInnerRef.current) { timelineInnerRef.current.style.transition = EASE; timelineInnerRef.current.style.transform = tx; }
    if (timelineTicksRef.current) { timelineTicksRef.current.style.transition = EASE; timelineTicksRef.current.style.transform = tx; }
    // Safety: never leave the timeline stuck if the load fails or the clicked region
    // turns out to have no footage (onLoaded never fires to release it).
    if (holdTimelineTimerRef.current) clearTimeout(holdTimelineTimerRef.current);
    holdTimelineTimerRef.current = setTimeout(() => { holdTimelineRef.current = false; }, 5000);
  };

  const handleTimelineMouseMove = (e) => {
    if (!timelineRef.current) return;

    if (timelineDragRef.current.active) {
      const dx = e.clientX - timelineDragRef.current.startX;
      if (Math.abs(dx) > 3) {
        if (!timelineDragRef.current.didDrag) setIsScrubbing(true); // once, on drag start
        timelineDragRef.current.didDrag = true;
        const w = timelineRef.current.offsetWidth;
        const pxS = w / (visibleMinutesRef.current * 60);
        if (pxS > 0) {
          const deltaSec = dx / pxS;
          const baseDur = getMappingDuration();
          const deltaVideoTime = deltaSec * (baseDur / Math.max(clipWindowDurationSeconds, 1));
          const newTime = Math.max(0, Math.min(baseDur, timelineDragRef.current.startTime - deltaVideoTime));
          scrubTimeline(newTime, baseDur);
        }
      }
      return;
    }

    if (!hoverLineRef.current || !hoverLabelRef.current) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const w = rect.width;
    const pxS = w / (visibleMinutesRef.current * 60);
    if (pxS === 0) return;

    const t = currentTimeRef.current;
    const baseDur = getMappingDuration();
    const elapsed = Math.floor((t / baseDur) * clipWindowDurationSeconds);
    const anchor = dayStartRef.current || clipWindow.start.startOf('day');
    const currentDaySecLive = clipWindow.start.add(elapsed, 'second').diff(anchor, 'second');
    const liveTranslateX = w / 2 - currentDaySecLive * pxS;
    const clickedDaySec = (x - liveTranslateX) / pxS;
    const hoverTime = anchor.add(Math.round(clickedDaySec), 'second');

    hoverLineRef.current.style.left = `${x}px`;
    hoverLineRef.current.style.display = 'block';

    const label = hoverTime.format('HH:mm:ss');
    hoverLabelRef.current.textContent = label;
    const labelW = hoverLabelRef.current.offsetWidth || 52;
    hoverLabelRef.current.style.left = `${Math.max(0, Math.min(w - labelW, x - labelW / 2))}px`;
    hoverLabelRef.current.style.display = 'block';
  };

  const handleTimelineMouseLeave = () => {
    if (hoverLineRef.current) hoverLineRef.current.style.display = 'none';
    if (hoverLabelRef.current) hoverLabelRef.current.style.display = 'none';
  };

  const handleTimelineClick = (e) => {
    if (timelineDragRef.current.didDrag) return;
    if (!timelineRef.current) return;
    const w = timelineRef.current.offsetWidth;
    const pxS = w / (visibleMinutesRef.current * 60);
    if (pxS === 0) return;

    // Compute live playhead position using clip start time (from API) + currentTimeRef offset
    const t = currentTimeRef.current;
    const baseDur = getMappingDuration();
    const elapsed = Math.floor((t / baseDur) * clipWindowDurationSeconds);
    const anchor = dayStartRef.current || clipWindow.start.startOf('day');
    const currentDaySecLive = clipWindow.start.add(elapsed, 'second').diff(anchor, 'second');
    const liveTranslateX = w / 2 - currentDaySecLive * pxS;

    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickedDaySec = (clickX - liveTranslateX) / pxS;
    const clickedAbsTime = anchor.add(Math.round(clickedDaySec), 'second');

    // If click is within current clip, seek within it
    const clipStartSec = clipWindow.start.diff(anchor, 'second');
    const clipEndSec = clipWindow.end.diff(anchor, 'second');
    if (clickedDaySec >= clipStartSec && clickedDaySec <= clipEndSec) {
      const ratio = (clickedDaySec - clipStartSec) / clipWindowDurationSeconds;
      seekTo(getMappingDuration() * ratio);
      return;
    }

    // Clicked outside the fetched window — that region isn't loaded yet. Ask the
    // parent to fetch a window at this time (same path as the date picker) so the
    // user can jump straight to a point that wasn't downloaded.
    if (clickedDaySec < loadedStartSec || clickedDaySec > loadedEndSec) {
      if (onDateChange) { glideTimelineToTime(clickedAbsTime); onDateChange(clickedAbsTime); }
      return;
    }

    // Inside the loaded window: find which loaded clip was clicked and select it.
    if (!onSelectClip || !Array.isArray(timelineData)) return;
    for (let i = 0; i < timelineData.length; i++) {
      const item = timelineData[i];
      try {
        let segStart = item?.recording_start_time ? dayjs(item.recording_start_time) : null;
        let segEnd = item?.recording_end_time ? dayjs(item.recording_end_time) : null;
        if (!segStart?.isValid() || !segEnd?.isValid()) {
          const url = item?.local_play_url || item?.play_url || item?.VideoUrl;
          if (!url) continue;
          const parsed = new URL(url);
          const fromTs = Number(parsed.searchParams.get('from_ts'));
          const rawDur = Number(parsed.searchParams.get('duration'));
          if (!Number.isFinite(fromTs) || fromTs <= 0) continue;
          segStart = dayjs.unix(fromTs);
          segEnd = segStart.add(Number.isFinite(rawDur) && rawDur > 0 ? rawDur : 900, 'second');
        }
        if (clickedAbsTime.isAfter(segStart) && clickedAbsTime.isBefore(segEnd)) {
          // Reaching here means the click is outside the current clip (the seek
          // branch above handled in-clip clicks), so this switches to another clip —
          // glide the timeline there while it loads.
          glideTimelineToTime(clickedAbsTime);
          onSelectClip(i, clickedAbsTime);
          return;
        }
      } catch (_) {}
    }
    // Inside the window but on a real gap (no recording) — nothing to play.
  };

  const togglePlay = async () => {
    const el = videoRef.current;
    if (!el) return;

    if (el.paused) {
      try {
        await el.play();
      } catch (_error) {
        setIsPlaying(false);
      }
    } else {
      el.pause();
    }
  };

  const seekTo = (nextTime) => {
    const el = videoRef.current;
    const baseDuration = getMappingDuration();
    if (!el || !baseDuration) return;
    const value = Math.max(0, Math.min(baseDuration, nextTime));
    el.currentTime = value;
    currentTimeRef.current = value; // keep ref in sync so next click uses correct position
    setCurrentTime(value);

    // Directly update timeline DOM so it works even when rAF is stopped (paused)
    if (timelineRef.current) {
      const w = timelineRef.current.offsetWidth;
      const pxS = w / (visibleMinutesRef.current * 60);
      if (pxS > 0) {
        const anchor = dayStartRef.current || clipWindow.start.startOf('day');
        const elapsed = Math.floor((value / baseDuration) * clipWindowDurationSeconds);
        const daySec = clipWindow.start.add(elapsed, 'second').diff(anchor, 'second');
        const tx = `translateX(${w / 2 - daySec * pxS}px)`;
        const EASE = 'transform 0.3s cubic-bezier(0.4,0,0.2,1)';
        if (timelineInnerRef.current) {
          timelineInnerRef.current.style.transition = EASE;
          timelineInnerRef.current.style.transform = tx;
        }
        if (timelineTicksRef.current) {
          timelineTicksRef.current.style.transition = EASE;
          timelineTicksRef.current.style.transform = tx;
        }
        setTimeout(() => {
          if (timelineInnerRef.current) timelineInnerRef.current.style.transition = '';
          if (timelineTicksRef.current) timelineTicksRef.current.style.transition = '';
        }, 350);
      }
    }
  };

  const changeSpeed = (e) => setSpeedAnchor(e.currentTarget);
  const selectSpeed = (s) => { setPlaybackRate(s); setSpeedAnchor(null); };

  const toggleMute = () => {
    const el = videoRef.current;
    if (!el) return;
    const nextMuted = !el.muted;
    el.muted = nextMuted;
    setIsMuted(nextMuted);
  };

  const onVolumeChange = (_e, nextValue) => {
    const el = videoRef.current;
    if (!el) return;
    const value = Number(nextValue);
    const clamped = Math.max(0, Math.min(1, value));
    setVolume(clamped);
    el.volume = clamped;
    if (clamped > 0 && isMuted) {
      el.muted = false;
      setIsMuted(false);
    }
  };

  const enterFullScreen = async () => {
    // Fullscreen the whole player (so the timeline + controls stay), not the raw video.
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      await document.exitFullscreen();
      return;
    }
    if (el.requestFullscreen) await el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
  };

  // Track fullscreen changes (incl. Esc to exit) and keep the controls shown outside
  // fullscreen.
  useEffect(() => {
    const onFsChange = () => {
      const fs = document.fullscreenElement === containerRef.current;
      setIsFullscreen(fs);
      setControlsVisible(true);
      if (controlsHideTimerRef.current) { clearTimeout(controlsHideTimerRef.current); controlsHideTimerRef.current = null; }
      if (fs) {
        // Hide the controls after a short idle even if the mouse never moves.
        controlsHideTimerRef.current = setTimeout(() => setControlsVisible(false), 2500);
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // In fullscreen, reveal the controls on pointer movement and hide them after a
  // short idle period (like a native video player).
  const handleContainerMouseMove = () => {
    if (!isFullscreen) return;
    setControlsVisible(true);
    if (controlsHideTimerRef.current) clearTimeout(controlsHideTimerRef.current);
    controlsHideTimerRef.current = setTimeout(() => setControlsVisible(false), 2500);
  };

  useEffect(() => () => {
    if (controlsHideTimerRef.current) clearTimeout(controlsHideTimerRef.current);
    if (holdTimelineTimerRef.current) clearTimeout(holdTimelineTimerRef.current);
  }, []);

  const showControls = !isFullscreen || controlsVisible;

  const openSharePanel = () => {
    // Scale default window with zoom: 30s at 15m, 60s at 30m, 2min at 1h, 12min at 6h, 24min at 12h
    const halfSec = Math.round((visibleMinutes / 15) * 30);
    let start = mappedTimelineDate.subtract(halfSec, 'second');
    let end = mappedTimelineDate.add(halfSec, 'second');

    if (start.isBefore(clipWindow.start)) {
      start = clipWindow.start;
      end = clipWindow.start.add(halfSec * 2, 'second');
    }
    if (end.isAfter(clipWindow.end)) {
      end = clipWindow.end;
      start = clipWindow.end.subtract(halfSec * 2, 'second');
    }
    if (start.isBefore(clipWindow.start)) start = clipWindow.start;

    setShareStart(start);
    setShareEnd(end);
    setShareExpiry('1 day');
    setShareReceiverName('');
    setShareEmail('');
    setShareComments('');
    setShareSending(false);
    setShareError('');
    setShareOpen(true);
  };

  const handleRangeHandleMouseDown = (e, which) => {
    e.preventDefault();
    e.stopPropagation();
    rangeHandleDragging.current = which;
    rangeHandleDragStartX.current = e.clientX;
    rangeHandleDragStartTimes.current = { start: shareStart, end: shareEnd };
  };

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!rangeHandleDragging.current || !timelineRef.current) return;
      const w = timelineRef.current.offsetWidth;
      const pxS = w / (visibleMinutesRef.current * 60);
      if (pxS <= 0) return;
      const dx = e.clientX - rangeHandleDragStartX.current;
      const deltaSec = Math.round(dx / pxS);
      const { start, end } = rangeHandleDragStartTimes.current;
      if (rangeHandleDragging.current === 'start') {
        setShareStart(start.add(deltaSec, 'second'));
      } else {
        setShareEnd(end.add(deltaSec, 'second'));
      }
    };
    const onMouseUp = () => {
      rangeHandleDragging.current = null;
      if (timelineDragRef.current.active) {
        timelineDragRef.current.active = false;
        if (timelineRef.current) timelineRef.current.style.cursor = 'pointer';
        // Commit the scrubbed position: land the video exactly and sync React state
        // once (the drag itself only touched the DOM / a throttled seek).
        if (timelineDragRef.current.didDrag) {
          if (seekRafRef.current) { cancelAnimationFrame(seekRafRef.current); seekRafRef.current = null; }
          const finalTime = currentTimeRef.current;
          const el = videoRef.current;
          if (el) el.currentTime = finalTime;
          pendingSeekRef.current = null;
          setCurrentTime(finalTime);
          setIsScrubbing(false);
        }
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const shareDurationSecs = shareStart && shareEnd ? Math.max(0, shareEnd.diff(shareStart, 'second')) : 60;
  const shareDurationLabel = (() => {
    const m = Math.floor(shareDurationSecs / 60);
    const s = shareDurationSecs % 60;
    if (m > 0 && s > 0) return `${m} minute${m > 1 ? 's' : ''} ${s} seconds`;
    if (m > 0) return `${m} minute${m > 1 ? 's' : ''}`;
    return `${s} seconds`;
  })();

  const onDateTimeChange = (value) => {
    if (!value) return;
    const selected = dayjs(value);
    if (!selected.isValid()) return;

    console.info('[picker] selected:', selected.format('YYYY-MM-DD HH:mm:ss'));
    console.info('[picker] clipWindow:', clipWindow.start.format('YYYY-MM-DD HH:mm:ss'), '→', clipWindow.end.format('YYYY-MM-DD HH:mm:ss'));

    // 1. Within current clip (handles cross-midnight clips correctly) → seek
    if (!selected.isBefore(clipWindow.start) && !selected.isAfter(clipWindow.end)) {
      const ratio = selected.diff(clipWindow.start, 'second') / clipWindowDurationSeconds;
      const baseDuration = getMappingDuration();
      console.info('[picker] case 1 — seek within current clip, ratio:', ratio.toFixed(4));
      seekTo(baseDuration * ratio);
      return;
    }

    // 2. Different clip in timelineData on any day → switch to it
    if (Array.isArray(timelineData)) {
      for (let i = 0; i < timelineData.length; i++) {
        const item = timelineData[i];
        try {
          let segStart = item?.recording_start_time ? dayjs(item.recording_start_time) : null;
          let segEnd = item?.recording_end_time ? dayjs(item.recording_end_time) : null;
          if (!segStart?.isValid() || !segEnd?.isValid()) {
            const url = item?.local_play_url || item?.play_url || item?.VideoUrl;
            if (!url) continue;
            const parsed = new URL(url);
            const fromTs = Number(parsed.searchParams.get('from_ts'));
            const rawDur = Number(parsed.searchParams.get('duration'));
            if (!Number.isFinite(fromTs) || fromTs <= 0) continue;
            segStart = dayjs.unix(fromTs);
            segEnd = segStart.add(Number.isFinite(rawDur) && rawDur > 0 ? rawDur : 900, 'second');
          }
          if (!selected.isBefore(segStart) && !selected.isAfter(segEnd)) {
            console.info('[picker] case 2 — switch to clip index', i, segStart.format('YYYY-MM-DD HH:mm:ss'), '→', segEnd.format('YYYY-MM-DD HH:mm:ss'));
            if (onSelectClip) { onSelectClip(i, selected); return; }
          }
        } catch (_) {}
      }
    }

    // 3. No matching clip loaded — fetch data for the selected day
    console.info('[picker] case 3 — fetch new date', selected.format('YYYY-MM-DD HH:mm:ss'));
    if (onDateChange) onDateChange(selected);
  };

  const volumeIcon = isMuted || volume === 0
    ? <VolumeOffRoundedIcon />
    : volume < 0.5
      ? <VolumeDownRoundedIcon />
      : <VolumeUpRoundedIcon />;

  const pickerPanelSx = {
    '& .MuiPaper-root': {
      borderRadius: 2,
      border: 1,
      borderColor: 'divider',
      bgcolor: 'background.paper'
    },
    '& .MuiMultiSectionDigitalClockSection-root, & .MuiDigitalClock-root': {
      overflowY: 'auto',
      scrollbarWidth: 'thin',
      scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.5)' : 'rgba(71,85,105,0.4)'} transparent`,
      '&::-webkit-scrollbar': { width: 5 },
      '&::-webkit-scrollbar-track': { background: 'transparent' },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.35)' : 'rgba(71,85,105,0.25)',
        borderRadius: 10,
        border: '1px solid transparent',
        backgroundClip: 'padding-box'
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.75)' : 'rgba(71,85,105,0.6)'
      }
    },
    '& .MuiMultiSectionDigitalClockSection-root .MuiMenuItem-root, & .MuiDigitalClock-root .MuiMenuItem-root': {
      borderRadius: 1,
      mx: 0.5,
      my: 0.25,
      fontSize: 13
    },
    '& .MuiMultiSectionDigitalClockSection-root .MuiMenuItem-root.Mui-selected, & .MuiDigitalClock-root .MuiMenuItem-root.Mui-selected': {
      bgcolor: 'primary.main',
      color: 'primary.contrastText'
    }
  };

  const handleSendLink = async () => {
    const expiryHours = { '1 hour': 1, '6 hours': 6, '1 day': 24, '3 days': 72, '7 days': 168, '15 days': 360 };
    const formData = new FormData();
    formData.append('access_link', `${process.env.REACT_APP_CLIENT_UI_URL}/shared-footage`);
    formData.append('expires_at', expiryHours[shareExpiry] ?? 24);
    formData.append('camera_id', cameraId ?? '');
    formData.append('from_time', shareStart?.format('YYYY-MM-DD HH:mm:ss') ?? '');
    formData.append('to_time', shareEnd?.format('YYYY-MM-DD HH:mm:ss') ?? '');
    formData.append('note', shareComments);
    formData.append('email', shareEmail);
    formData.append('reciever_name', shareReceiverName);
    
    formData.append('link_type', "archives");

    setShareSending(true);
    setShareError('');
    try {
      await axios.post(getURL('api-access-links/add'), formData);
      setShareOpen(false);
    } catch (err) {
      setShareError(err?.response?.data?.detail || 'Failed to send link. Please try again.');
    } finally {
      setShareSending(false);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
      return;
    }
    navigate(-1);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        ref={containerRef}
        onMouseMove={handleContainerMouseMove}
        sx={{
          position: embedded ? 'relative' : 'fixed',
          inset: embedded ? 'auto' : 0,
          width: '100%',
          height: '100%',
          bgcolor: 'background.default',
          color: 'text.primary',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          overflow: 'hidden',
          cursor: showControls ? 'default' : 'none'
        }}
      >
        {/* Player area */}
        <Box sx={{ flex: 1, minHeight: 0, position: 'relative', display: 'grid', gridTemplateRows: isFullscreen ? '1fr' : 'auto minmax(0,1fr) auto', overflow: 'hidden', minWidth: 0 }}>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: { xs: 1, sm: 2 },
            py: 0.75,
            ...(isFullscreen && {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 20,
              // Theme-adaptive scrim so the theme-coloured controls stay readable.
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0))'
                : 'linear-gradient(to bottom, rgba(255,255,255,0.85), rgba(255,255,255,0))',
              opacity: showControls ? 1 : 0,
              pointerEvents: showControls ? 'auto' : 'none',
              transition: 'opacity 0.25s ease'
            })
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {sharedMode ? (
              <>
                <Logo sx={{ width: 72 }} />
                <Box sx={{ width: '1px', height: 16, bgcolor: 'divider' }} />
              </>
            ) : (
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'primary.main' }} />
            )}
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: 'text.primary' }}>{cameraLabel}</Typography>
          </Stack>
          {!sharedMode && (
            <IconButton size="small" sx={controlBtnSx} onClick={handleClose}>
              <CloseRoundedIcon />
            </IconButton>
          )}
        </Stack>

        {/* Video */}
        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          <Box sx={{ position: 'relative', aspectRatio: '16 / 9', height: '100%', maxWidth: '100%', overflow: 'hidden' }}>
            <Box
              component="video"
              ref={videoRef}
              muted
              playsInline
              preload="metadata"
              sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
            {(loading || isBuffering) && !isScrubbing && !playbackError && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0,0,0,0.35)',
                  zIndex: 2,
                  pointerEvents: 'none'
                }}
              >
                <CircularProgress size={44} thickness={4} sx={{ color: '#fff' }} />
              </Box>
            )}
            {playbackError && (
              <Box sx={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', px: 1.5, py: 1, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.72)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', fontSize: 12, whiteSpace: 'nowrap' }}>
                {playbackError}
              </Box>
            )}
          </Box>
        </Box>

        {/* Bottom controls */}
        <Box sx={{
          px: { xs: 0.75, sm: 1.5 },
          pt: 0.5,
          pb: 1,
          borderTop: 1,
          borderColor: 'divider',
          ...(isFullscreen && {
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 20,
            borderTop: 0,
            // Theme-adaptive translucent bar so the theme-coloured controls (icons,
            // timeline, date/time) keep their contrast in both light and dark.
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(20,26,33,0.72)' : 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(2px)',
            opacity: showControls ? 1 : 0,
            pointerEvents: showControls ? 'auto' : 'none',
            transition: 'opacity 0.25s ease'
          })
        }}>

          {/* 24-hour Timeline row */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto minmax(0,1fr) auto', alignItems: 'center', gap: 1, mb: 0.25 }}>
            <IconButton
              size="small"
              disabled={!hasPrevChunk}
              onClick={() => { if (onPrevChunk) { onPrevChunk(); return; } seekTo(currentTime - clipWindowDurationSeconds); }}
              sx={{
                width: 22, height: 22, borderRadius: 0.75,
                color: 'text.secondary',
                border: '1px solid', borderColor: 'divider',
                bgcolor: 'action.hover',
                '& .MuiSvgIcon-root': { fontSize: 15 },
                '&:hover': { color: 'text.primary', borderColor: 'text.secondary', bgcolor: 'action.selected' },
                '&.Mui-disabled': { opacity: 0.3 }
              }}
            >
              <ArrowBackRoundedIcon />
            </IconButton>

            {/* Scrolling 24-hr track */}
            <Box
              ref={timelineRef}
              onMouseDown={handleTimelineMouseDown}
              onClick={handleTimelineClick}
              onMouseMove={handleTimelineMouseMove}
              onMouseLeave={handleTimelineMouseLeave}
              sx={{ position: 'relative', height: 48, overflow: 'hidden', cursor: 'pointer', userSelect: 'none' }}
            >
              {/* Loading segments — thin indeterminate bar for perceived responsiveness */}
              {loading && (
                <LinearProgress
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 2,
                    zIndex: 5,
                    pointerEvents: 'none',
                    bgcolor: 'transparent',
                    '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' },
                  }}
                />
              )}
              {/* Time labels row */}
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 14, pointerEvents: 'none', overflow: 'hidden' }}>
                <Box ref={timelineInnerRef} sx={{ position: 'absolute', top: 0, left: 0, width: `${innerWidth}px`, transform: `translateX(${translateX}px)` }}>
                  {pxPerSec > 0 && (() => {
                    const DAY = 24 * 3600;
                    const lblInterval = getLabelInterval(visibleMinutes);
                    const totalSecs = Math.max(DAY, clipEndDaySec + 3600);
                    // Extend the scale a full day before and after so the ruler reads
                    // continuously across midnight — previous and next day are shown
                    // instead of the timeline dead-ending at 00:00 / 24:00.
                    const startSec = -DAY;
                    const endSec = totalSecs + DAY;
                    const count = Math.ceil((endSec - startSec) / lblInterval) + 1;
                    return Array.from({ length: count }, (_, i) => startSec + i * lblInterval).map((sec) => {
                      const labelDate = dayStart.add(sec, 'second');
                      const isMidnight = labelDate.hour() === 0 && labelDate.minute() === 0;
                      return (
                        <Typography
                          key={sec}
                          component="span"
                          sx={{
                            position: 'absolute',
                            left: `${sec * pxPerSec}px`,
                            top: 0,
                            fontSize: 9,
                            fontWeight: isMidnight ? 700 : 400,
                            color: isMidnight ? 'text.secondary' : 'text.disabled',
                            transform: 'translateX(-50%)',
                            whiteSpace: 'nowrap',
                            lineHeight: '14px',
                            display: 'block'
                          }}
                        >
                          {/* Mark day boundaries with the date so the day is unambiguous */}
                          {labelDate.format(isMidnight ? 'MMM D' : 'HH:mm')}
                        </Typography>
                      );
                    });
                  })()}
                </Box>
              </Box>

              {/* Tick + segment area */}
              <Box sx={{ position: 'absolute', top: 14, left: 0, right: 0, bottom: 0, overflow: 'hidden' }}>
                {/* Scrolling inner */}
                <Box ref={timelineTicksRef} sx={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${innerWidth}px`, transform: `translateX(${translateX}px)` }}>
                  {/* Base track — all clips (loaded or not-loaded) render the same dim
                      gray, like the un-played part of a normal video player's bar. */}
                  {[...notLoadedZones, ...recordingRuns].map((zone, idx) => (
                    <Box
                      key={`base-${idx}`}
                      sx={{
                        position: 'absolute',
                        left: `${zone.startDaySec * pxPerSec}px`,
                        width: `${Math.max(zone.durationSec * pxPerSec, 1)}px`,
                        top: 0,
                        height: 3,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.16)',
                      }}
                    />
                  ))}
                  {/* Cache — how much of the current clip is actually buffered, shown as
                      the lighter/prominent gray (the "loaded" part of the bar). */}
                  {bufferedRanges.map((r, idx) => (
                    <Box
                      key={`buf-${idx}`}
                      sx={{
                        position: 'absolute',
                        left: `${r.startDaySec * pxPerSec}px`,
                        width: `${Math.max(r.durationSec * pxPerSec, 1)}px`,
                        top: 0,
                        height: 3,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)',
                      }}
                    />
                  ))}
                  {/* Gap zones — no recording available (red) */}
                  {gapZones.map((gap, idx) => (
                    <Box
                      key={`gap-${idx}`}
                      sx={{
                        position: 'absolute',
                        left: `${gap.startDaySec * pxPerSec}px`,
                        width: `${Math.max(gap.durationSec * pxPerSec, 1)}px`,
                        top: 0,
                        height: 3,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(239,68,68,0.72)' : 'rgba(220,38,38,0.62)',
                      }}
                    />
                  ))}
                  {/* Event segment boxes */}
                  {segmentBoxes24.filter(seg => seg.isEvent).map((seg, idx) => (
                    <Box
                      key={`evt-${idx}`}
                      sx={{
                        position: 'absolute',
                        left: `${seg.startDaySec * pxPerSec}px`,
                        width: `${Math.max(seg.durationSec * pxPerSec, 2)}px`,
                        top: 0,
                        height: 3,
                        bgcolor: 'error.main',
                        opacity: 0.95,
                      }}
                    />
                  ))}
                  {/* Tick marks — adaptive intervals based on zoom level */}
                  {(() => {
                    const tk = getTickIntervals(visibleMinutes);
                    const c = theme.palette.mode === 'dark';
                    const DAY = 24 * 3600;
                    const totalSecs = Math.max(DAY, clipEndDaySec + 3600);
                    // Span the grid a day either side of the selected day so the tick
                    // marks continue across midnight (aligned with the extended labels).
                    return (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 3,
                          left: `${-DAY * pxPerSec}px`,
                          width: `${(totalSecs + 2 * DAY) * pxPerSec}px`,
                          height: 'calc(100% - 3px)',
                          backgroundImage: `
                            linear-gradient(to right, ${theme.palette.divider}, ${theme.palette.divider}),
                            repeating-linear-gradient(to right, ${c ? 'rgba(100,130,150,0.5)' : 'rgba(71,85,105,0.35)'} 0 1px, transparent 1px ${pxPerSec * tk.small}px),
                            repeating-linear-gradient(to right, ${c ? 'rgba(130,160,180,0.75)' : 'rgba(71,85,105,0.55)'} 0 1px, transparent 1px ${pxPerSec * tk.medium}px),
                            repeating-linear-gradient(to right, ${c ? 'rgba(155,180,200,0.95)' : 'rgba(51,65,85,0.8)'} 0 1px, transparent 1px ${pxPerSec * tk.large}px)
                          `,
                          backgroundSize: `100% 1px, ${pxPerSec * tk.small}px 6px, ${pxPerSec * tk.medium}px 11px, ${pxPerSec * tk.large}px 20px`,
                          backgroundPosition: '0 0, 0 0, 0 0, 0 0',
                          backgroundRepeat: 'no-repeat, repeat-x, repeat-x, repeat-x'
                        }}
                      />
                    );
                  })()}
                  {/* Share clip range selector handles */}
                  {shareOpen && shareStart && shareEnd && pxPerSec > 0 && (() => {
                    const startDaySec = shareStart.diff(dayStart, 'second');
                    const endDaySec = shareEnd.diff(dayStart, 'second');
                    const rangeLeft = startDaySec * pxPerSec;
                    const rangeWidth = Math.max(0, (endDaySec - startDaySec) * pxPerSec);
                    const orange = theme.palette.warning.main;
                    const handleSx = {
                      position: 'absolute',
                      top: 0,
                      width: 7,
                      height: '100%',
                      transform: 'translateX(-50%)',
                      cursor: 'ew-resize',
                      zIndex: 5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: orange,
                      borderRadius: '99px',
                    };
                    return (
                      <>
                        {/* Range fill — light yellow tint */}
                        <Box sx={{ position: 'absolute', left: `${rangeLeft}px`, width: `${rangeWidth}px`, top: 0, height: '100%', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,220,80,0.18)' : 'rgba(234,179,8,0.22)', zIndex: 3, pointerEvents: 'none' }} />
                        {/* Start handle */}
                        <Box onMouseDown={(e) => handleRangeHandleMouseDown(e, 'start')} sx={{ ...handleSx, left: `${rangeLeft}px` }}>
                          <DragHandleSharp sx={{ fontSize: 11, color: 'rgba(0,0,0,0.65)', transform: 'rotate(90deg)' }} />
                        </Box>
                        {/* End handle */}
                        <Box onMouseDown={(e) => handleRangeHandleMouseDown(e, 'end')} sx={{ ...handleSx, left: `${rangeLeft + rangeWidth}px` }}>
                          <DragHandleSharp sx={{ fontSize: 11, color: 'rgba(0,0,0,0.65)', transform: 'rotate(90deg)' }} />
                        </Box>
                      </>
                    );
                  })()}
                </Box>
                {/* Playhead — always centered */}
                <Box sx={{
                  position: 'absolute',
                  left: '50%',
                  top: 0,
                  bottom: 0,
                  width: 2,
                  transform: 'translateX(-50%)',
                  bgcolor: theme.palette.mode === 'dark' ? '#90caf9' : '#1565c0',
                  boxShadow: theme.palette.mode === 'dark' ? '0 0 6px rgba(144,202,249,0.7)' : '0 0 6px rgba(21,101,192,0.6)',
                  borderRadius: 1,
                  zIndex: 2,
                  pointerEvents: 'none'
                }} />
              </Box>

              {/* Hover line */}
              <Box
                ref={hoverLineRef}
                sx={{
                  display: 'none',
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  width: '1px',
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.35)',
                  zIndex: 3,
                  pointerEvents: 'none',
                  transform: 'translateX(-50%)',
                }}
              />
              {/* Hover time label */}
              <Box
                ref={hoverLabelRef}
                sx={{
                  display: 'none',
                  position: 'absolute',
                  top: 2,
                  px: '4px',
                  py: '1px',
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.85)' : 'rgba(15,23,42,0.78)',
                  color: '#e2e8f0',
                  fontSize: 9,
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  borderRadius: '3px',
                  border: '1px solid rgba(255,255,255,0.12)',
                  zIndex: 4,
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap',
                  lineHeight: '14px',
                }}
              />
            </Box>

            <IconButton
              size="small"
              disabled={!hasNextChunk}
              onClick={() => { if (onNextChunk) { onNextChunk(); return; } seekTo(currentTime + clipWindowDurationSeconds); }}
              sx={{
                width: 22, height: 22, borderRadius: 0.75,
                color: 'text.secondary',
                border: '1px solid', borderColor: 'divider',
                bgcolor: 'action.hover',
                '& .MuiSvgIcon-root': { fontSize: 15 },
                '&:hover': { color: 'text.primary', borderColor: 'text.secondary', bgcolor: 'action.selected' },
                '&.Mui-disabled': { opacity: 0.3 }
              }}
            >
              <ArrowForwardRoundedIcon />
            </IconButton>
          </Box>
          {/* Playback controls, date/time picker and zoom — one responsive row */}
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            {/* Center: date/time + zoom (order keeps it between the control groups) */}
            <Box sx={{ order: 2, flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
              {/* Clickable date+time — pinned to the row centre so it sits on the playhead */}
              <Box sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 1 }}>
                <Box
                  onClick={() => {
                    if (sharedMode) return;
                    setPickerDraft(mappedTimelineDate);
                    setPickerOpen(true);
                  }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    cursor: 'pointer', px: 0.75, py: 0.25, borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 9, color: 'text.disabled', lineHeight: 1.2, letterSpacing: '0.04em' }}>
                      {mappedTimelineDate.format('DD MMM YYYY')}
                    </Typography>
                    <Typography
                      ref={timeLabelRef}
                      sx={{ fontSize: 13, fontFamily: 'monospace', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}
                    >
                      {mappedTimelineDate.format('HH:mm:ss')}
                    </Typography>
                  </Box>
                  <KeyboardArrowUpRoundedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                </Box>
                {/* Hidden picker — textField anchors the popper */}
                <DesktopDateTimePicker
                  open={pickerOpen}
                  onClose={() => { setPickerDraft(null); setPickerOpen(false); }}
                  value={pickerDraft ?? mappedTimelineDate}
                  onChange={(v) => { if (v && v.isValid()) setPickerDraft(v); }}
                  onAccept={(v) => { if (v && v.isValid()) onDateTimeChange(v); setPickerDraft(null); setPickerOpen(false); }}
                  minDateTime={dayjs().subtract(30, 'day').startOf('day')}
                  maxDateTime={pickerMaxDateTime}
                  format="DD MMM YYYY  HH:mm"
                  views={['year', 'month', 'day', 'hours', 'minutes']}
                  ampm={false}
                  slotProps={{
                    popper: { container: isFullscreen ? containerRef.current : undefined, placement: 'top', sx: { zIndex: 1600, ...pickerPanelSx } },
                    desktopPaper: { sx: pickerPanelSx },
                    digitalClockSectionItem: {
                      sx: {
                        borderRadius: 1, mx: 0.5, my: 0.25, fontSize: 13,
                        '&.Mui-selected': { bgcolor: 'primary.main', color: 'primary.contrastText' }
                      }
                    },
                    digitalClockSection: {
                      sx: {
                        overflowY: 'auto', scrollbarWidth: 'thin',
                        scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.5)' : 'rgba(71,85,105,0.4)'} transparent`,
                        '&::-webkit-scrollbar': { width: 5 },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.35)' : 'rgba(71,85,105,0.25)',
                          borderRadius: 10, border: '1px solid transparent', backgroundClip: 'padding-box'
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.75)' : 'rgba(71,85,105,0.6)'
                        }
                      }
                    },
                    textField: {
                      sx: {
                        position: 'absolute', bottom: 0, left: '50%',
                        width: 1, height: 1, opacity: 0,
                        pointerEvents: 'none', overflow: 'hidden'
                      }
                    }
                  }}
                />
              </Box>

              {/* Zoom chips — only shown while the share panel is open */}
              {shareOpen && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                {availableZoomLevels.map(({ m, label }) => (
                  <Box
                    key={m}
                    onClick={() => setVisibleMinutes(m)}
                    sx={{
                      px: 0.75, py: 0.1, borderRadius: 1, fontSize: 9,
                      fontWeight: visibleMinutes === m ? 700 : 400,
                      color: visibleMinutes === m ? 'primary.contrastText' : 'text.secondary',
                      bgcolor: visibleMinutes === m ? 'primary.main' : 'transparent',
                      border: '1px solid',
                      borderColor: visibleMinutes === m ? 'primary.main' : 'divider',
                      cursor: 'pointer', lineHeight: '14px', userSelect: 'none',
                      '&:hover': { borderColor: 'primary.main', color: visibleMinutes === m ? 'primary.contrastText' : 'primary.main' }
                    }}
                  >
                    {label}
                  </Box>
                ))}
              </Box>
              )}
            </Box>

            {/* Left: playback controls */}
            <Stack direction="row" alignItems="center" spacing={0.25} sx={{ order: 1, flexShrink: 0 }}>
              <IconButton sx={controlBtnSx} onClick={togglePlay}>
                {isPlaying ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
              </IconButton>
              <IconButton sx={controlBtnSx} onClick={() => seekTo(currentTime - 10)}><Replay10RoundedIcon /></IconButton>
              <IconButton sx={controlBtnSx} onClick={() => seekTo(currentTime + 10)}><Forward10RoundedIcon /></IconButton>
              <IconButton
                sx={{ ...controlBtnSx, display: { xs: 'none', sm: 'inline-flex' } }}
                disabled={!hasPrevChunk}
                onClick={() => { if (onPrevChunk) onPrevChunk(); }}
              ><SkipPreviousRoundedIcon /></IconButton>
              <IconButton
                sx={{ ...controlBtnSx, display: { xs: 'none', sm: 'inline-flex' } }}
                disabled={!hasNextChunk}
                onClick={() => { if (onNextChunk) onNextChunk(); }}
              ><SkipNextRoundedIcon /></IconButton>
              <IconButton sx={{ ...controlBtnSx, position: 'relative' }} onClick={changeSpeed}>
                <SpeedRoundedIcon />
                <Typography sx={{ fontSize: 7, position: 'absolute', bottom: 1, right: 2, color: 'primary.main', lineHeight: 1, fontWeight: 700 }}>
                  {playbackRate % 1 === 0 ? `${playbackRate}x` : `${playbackRate}x`}
                </Typography>
              </IconButton>
              <Popover
                open={Boolean(speedAnchor)}
                anchorEl={speedAnchor}
                onClose={() => setSpeedAnchor(null)}
                container={isFullscreen ? containerRef.current : undefined}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                slotProps={{
                  paper: {
                    sx: {
                      p: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                      bgcolor: 'background.paper',
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 8px 32px rgba(0,0,0,0.6)'
                        : '0 8px 32px rgba(0,0,0,0.15)'
                    }
                  }
                }}
              >
                <Typography sx={{ fontSize: 10, color: 'text.disabled', mb: 0.75, textAlign: 'center', letterSpacing: '0.06em' }}>
                  PLAYBACK SPEED
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.5 }}>
                  {SPEEDS.map((s) => (
                    <Box
                      key={s}
                      onClick={() => selectSpeed(s)}
                      sx={{
                        px: 0.75, py: 0.4, borderRadius: 1, fontSize: 11, textAlign: 'center',
                        fontWeight: playbackRate === s ? 700 : 400,
                        color: playbackRate === s ? 'primary.contrastText' : 'text.secondary',
                        bgcolor: playbackRate === s ? 'primary.main' : 'transparent',
                        border: '1px solid',
                        borderColor: playbackRate === s ? 'primary.main' : 'divider',
                        cursor: 'pointer', userSelect: 'none',
                        '&:hover': { borderColor: 'primary.main', color: playbackRate === s ? 'primary.contrastText' : 'primary.main' }
                      }}
                    >
                      {s}x
                    </Box>
                  ))}
                </Box>
              </Popover>
              <IconButton sx={controlBtnSx} onClick={toggleMute}>{volumeIcon}</IconButton>
              <Slider
                min={0}
                max={1}
                step={0.01}
                value={isMuted ? 0 : volume}
                onChange={onVolumeChange}
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  width: { sm: 56, md: 72 },
                  color: 'text.secondary',
                  '& .MuiSlider-rail': { opacity: 0.35 },
                  '& .MuiSlider-thumb': {
                    width: 11,
                    height: 11,
                    bgcolor: theme.palette.mode === 'dark' ? '#b4c3ce' : '#64748b',
                    border: `1px solid ${theme.palette.mode === 'dark' ? '#2d4454' : '#475569'}`
                  }
                }}
              />
          </Stack>

            {/* Right: share + fullscreen */}
            <Stack direction="row" alignItems="center" sx={{ order: 3, flexShrink: 0 }}>
              {!sharedMode && (
                <Tooltip title="Share clip">
                  <IconButton sx={controlBtnSx} onClick={openSharePanel}>
                    <IosShareRoundedIcon />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton sx={controlBtnSx} onClick={enterFullScreen}>
                <FullscreenRoundedIcon />
              </IconButton>
            </Stack>
          </Box>
        </Box>
        </Box>
        {/* Share / Archive Clip panel — docked right, flex sibling */}
        {shareOpen && (
          <Box
            ref={sharePanelRef}
            sx={{
              width: { xs: '100%', sm: 310 },
              height: { xs: 'auto', sm: '100%' },
              maxHeight: { xs: '55%', sm: 'none' },
              flexShrink: 0,
              bgcolor: 'background.paper',
              borderLeft: { xs: 'none', sm: '1px solid' },
              borderTop: { xs: '1px solid', sm: 'none' },
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Panel header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Archive Clip</Typography>
              <IconButton size="small" sx={controlBtnSx} onClick={() => setShareOpen(false)}>
                <CloseRoundedIcon />
              </IconButton>
            </Box>

            {/* Scrollable body */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
              {/* Time range chips */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box sx={{ flex: 1, textAlign: 'center', p: 1, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: 'monospace', color: 'text.primary', lineHeight: 1.4 }}>
                    {shareStart?.format('HH:mm:ss')}
                  </Typography>
                  <Typography sx={{ fontSize: 9, color: 'text.disabled', lineHeight: 1.3 }}>
                    {shareStart?.format('ddd MMM DD YYYY')}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 14, color: 'text.secondary', fontWeight: 600, flexShrink: 0 }}>→</Typography>
                <Box sx={{ flex: 1, textAlign: 'center', p: 1, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: 'monospace', color: 'text.primary', lineHeight: 1.4 }}>
                    {shareEnd?.format('HH:mm:ss')}
                  </Typography>
                  <Typography sx={{ fontSize: 9, color: 'text.disabled', lineHeight: 1.3 }}>
                    {shareEnd?.format('ddd MMM DD YYYY')}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography sx={{ fontSize: 13, color: 'text.primary' }}>Link Expires</Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select value={shareExpiry} onChange={(e) => setShareExpiry(e.target.value)} sx={{ fontSize: 12 }}>
                    {['1 hour', '6 hours', '1 day', '3 days', '7 days', '15 days'].map((v) => (
                      <MenuItem key={v} value={v} sx={{ fontSize: 12 }}>{v}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <TextField
                fullWidth
                size="small"
                label="Recipient Name"
                placeholder="Enter recipient name…"
                value={shareReceiverName}
                onChange={(e) => setShareReceiverName(e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                size="small"
                label="Email"
                placeholder="recipient@example.com"
                type="email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                size="small"
                label="Comments"
                placeholder="Add a note…"
                multiline
                minRows={3}
                value={shareComments}
                onChange={(e) => setShareComments(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Typography sx={{ fontSize: 11, color: 'text.disabled', lineHeight: 1.5 }}>
                Shared video and features can be viewed by anyone with access to the link URL
              </Typography>
            </Box>

            {/* Footer */}
            <Box sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
              {shareError && (
                <Typography sx={{ fontSize: 11, color: 'error.main', mb: 1, lineHeight: 1.4 }}>{shareError}</Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  Duration: {shareDurationLabel}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  disabled={shareSending}
                  sx={{ borderRadius: 1.5, minWidth: 86 }}
                  onClick={handleSendLink}
                >
                  {shareSending ? 'Sending…' : 'Send Link'}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
}
